/**
 * WHICH Google account did the tenant just connect? (plan 3c interface D13.)
 *
 * Drive's about.get accepts the drive.file scope -- the ONLY scope this
 * platform client requests (api/src/googleOAuth.ts's GOOGLE_DRIVE_FILE_SCOPE)
 * -- and returns the signed-in user's e-mail and display name. So the identity
 * can be shown without adding a scope, without a consent-screen change and
 * without a new API route.
 *
 * The access token comes from POST /admin-api/google/picker-token, is minted
 * server-side from the stored refresh token, is drive.file-scoped and lives
 * about an hour (api/src/adminRoutes.ts's picker-token handler). It is passed
 * in as an argument, held for the duration of one call, and never stored --
 * ESLint bans localStorage/sessionStorage in this package in all four
 * spellings for exactly this reason.
 *
 * A failure here is NOT a failed connect: the connection is already stored. It
 * returns null and the UI says the account name could not be read, which is
 * weaker than naming it and much better than claiming a connect failed -- or
 * than implying an identity nobody verified.
 *
 * THIS IS THE SPA'S ONE NON-SAME-ORIGIN REQUEST, and it is the reason
 * lib/api.ts's `adminFetch` REFUSES absolute URLs rather than quietly allowing
 * them: the exception is here, visible, in a module named after it. Do not
 * route this through `adminFetch`, and do not weaken `adminFetch` to admit it.
 */

export interface GoogleAccount {
  email: string | null;
  displayName: string | null;
}

const ABOUT_URL = 'https://www.googleapis.com/drive/v3/about?fields=user(emailAddress,displayName)';

export async function fetchGoogleAccount(
  accessToken: string,
  fetchImpl: typeof fetch = fetch,
): Promise<GoogleAccount | null> {
  try {
    const res = await fetchImpl(ABOUT_URL, {
      headers: { Authorization: `Bearer ${accessToken}` },
      // Stated, not defaulted: this request must carry no ambient credential
      // of ours to a third-party origin. The bearer token is the whole auth.
      credentials: 'omit',
    });
    if (!res.ok) return null;
    const body = (await res.json()) as { user?: { emailAddress?: string; displayName?: string } };
    return {
      email: body.user?.emailAddress ?? null,
      displayName: body.user?.displayName ?? null,
    };
  } catch {
    return null;
  }
}
